import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clampPosition, Point, Rect, resizeRect, ResizeEdge, Size } from '../../hooks/windowManager';
import styles from './Window.module.css';

export type SheetType = 'terminal' | 'paper' | 'signal' | 'human';

interface WindowProps {
    title: string;
    sheet: SheetType;
    rect?: Rect;
    minSize: Size;
    bounds: Size;
    focused: boolean;
    layer: number;
    compact: boolean;
    drifting?: boolean;
    settling?: boolean;
    /** 눌러서 펼친 상태. 읽는 동안은 옮기거나 크기를 바꾸지 않는다 */
    expanded?: boolean;
    onCollapse?: () => void;
    onFocus: () => void;
    onClose: () => void;
    onMove: (position: Point) => void;
    onResize: (rect: Rect) => void;
    children: React.ReactNode;
}

const CLOSE_MS = 160;
const DRAG_THRESHOLD = 4;
const NOT_DRAGGABLE = 'button, input, label, textarea';
// 왼쪽 위 모서리는 닫기 버튼 자리라 위쪽, 왼쪽 변으로 조절한다
const EDGES: ResizeEdge[] = ['n', 'e', 's', 'w', 'ne', 'se', 'sw'];
/** 독 아이콘 줄의 세로 중심. 패널 영역 아래로 이만큼 내려가 있다 */
const DOCK_OFFSET = 50;
/** 좁은 화면의 독 토글 중심. 화면 오른쪽 아래 모서리에서 잰다 */
const COMPACT_DOCK_OFFSET = 40;

const swallowClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
};

/**
 * 독과 같은 유리판에 시트를 끼운 패널.
 * 제목줄 없이 어디든 끌어서 옮기고, 가장자리를 끌어 크기를 바꾼다.
 * 마우스를 올리면 닫기가 나타나고, 열리고 닫힐 때는 독 쪽을 축으로 움직인다.
 * 좁은 화면에서는 영역을 꽉 채우고 독으로 전환한다.
 */
export const Window: React.FC<WindowProps> = ({
    title,
    sheet,
    rect,
    minSize,
    bounds,
    focused,
    layer,
    compact,
    drifting = false,
    settling = false,
    expanded = false,
    onCollapse,
    onFocus,
    onClose,
    onMove,
    onResize,
    children
}) => {
    const windowRef = useRef<HTMLElement>(null);
    const dragRef = useRef<{ pointerX: number; pointerY: number; x: number; y: number; active: boolean } | null>(null);
    const stopDragRef = useRef<(() => void) | null>(null);
    const resizeRef = useRef<{ edge: ResizeEdge; pointerX: number; pointerY: number; start: Rect } | null>(null);
    const liveRef = useRef<Rect | null>(null);
    const [liveRect, setLiveRect] = useState<Rect | null>(null);
    const [closing, setClosing] = useState(false);

    // 놓는 순간엔 렌더가 아직 따라오지 못했을 수 있어, 마지막 값은 ref에서 읽는다
    const updateLive = (next: Rect | null) => {
        liveRef.current = next;
        setLiveRect(next);
    };

    const fitted = rect && {
        width: Math.min(rect.width, bounds.width),
        height: Math.min(rect.height, bounds.height),
    };
    const box = liveRect ?? (rect && fitted ? { ...clampPosition(rect, fitted, bounds), ...fitted } : null);

    // 앞으로 나온 패널로 키보드 초점을 옮긴다
    useEffect(() => {
        const element = windowRef.current;
        if (focused && element && !element.contains(document.activeElement)) {
            element.focus({ preventScroll: true });
        }
    }, [focused]);

    const close = useCallback(() => {
        setClosing(true);
        window.setTimeout(onClose, CLOSE_MS);
    }, [onClose]);

    // Esc: 맨 위 패널 닫기
    useEffect(() => {
        if (!focused || compact) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            if (expanded && onCollapse) onCollapse();
            else close();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [focused, compact, close, expanded, onCollapse]);

    const finishDrag = () => {
        const drag = dragRef.current;
        if (!drag) return;
        dragRef.current = null;

        if (drag.active) {
            if (liveRef.current) onMove(liveRef.current);

            // 링크 위에서 시작한 드래그가 끝나도 링크가 열리지 않게 한다
            const element = windowRef.current;
            element?.addEventListener('click', swallowClick, { capture: true, once: true });
            window.setTimeout(() => element?.removeEventListener('click', swallowClick, true), 0);
        }
        updateLive(null);
    };

    // 빠르게 끌면 첫 움직임부터 패널 밖에서 일어나므로 창 전체에서 듣는다
    const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
        if (compact || expanded || !box || event.button !== 0 || (event.target as Element).closest(NOT_DRAGGABLE)) return;

        const start = box;
        const section = event.currentTarget;
        const { pointerId } = event;
        dragRef.current = { pointerX: event.clientX, pointerY: event.clientY, x: start.x, y: start.y, active: false };

        const stop = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', stop);
            window.removeEventListener('pointercancel', stop);
            section.removeEventListener('lostpointercapture', stop);
            stopDragRef.current = null;
            finishDrag();
        };

        const handleMove = (moveEvent: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;

            // 창 밖에서 버튼을 놓아 끝을 받지 못한 경우
            if (moveEvent.buttons === 0) {
                stop();
                return;
            }

            const dx = moveEvent.clientX - drag.pointerX;
            const dy = moveEvent.clientY - drag.pointerY;
            if (!drag.active) {
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
                drag.active = true;
                section.setPointerCapture(pointerId);
            }

            updateLive({ ...start, ...clampPosition({ x: drag.x + dx, y: drag.y + dy }, start, bounds) });
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
        section.addEventListener('lostpointercapture', stop);
        stopDragRef.current = stop;
    };

    useEffect(() => () => stopDragRef.current?.(), []);

    const finishResize = () => {
        if (!resizeRef.current) return;
        resizeRef.current = null;
        if (liveRef.current) onResize(liveRef.current);
        updateLive(null);
    };

    const handleResizeStart = (edge: ResizeEdge) => (event: React.PointerEvent<HTMLElement>) => {
        if (!box || event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        resizeRef.current = { edge, pointerX: event.clientX, pointerY: event.clientY, start: box };
    };

    const handleResizeMove = (event: React.PointerEvent<HTMLElement>) => {
        const resize = resizeRef.current;
        if (!resize) return;
        event.stopPropagation();

        if (event.buttons === 0) {
            finishResize();
            return;
        }

        updateLive(resizeRect(
            resize.start,
            resize.edge,
            { x: event.clientX - resize.pointerX, y: event.clientY - resize.pointerY },
            minSize,
            bounds,
        ));
    };

    const handleResizeEnd = (event: React.PointerEvent<HTMLElement>) => {
        if (!resizeRef.current) return;
        event.stopPropagation();
        finishResize();
    };

    const dockOrigin = compact
        ? `${bounds.width - COMPACT_DOCK_OFFSET}px ${bounds.height - COMPACT_DOCK_OFFSET}px`
        : `${bounds.width / 2}px ${bounds.height + DOCK_OFFSET}px`;

    return (
        <section
            ref={windowRef}
            className={[
                styles.window,
                focused ? styles.focused : '',
                closing ? styles.closing : '',
                liveRect ? styles.dragging : '',
                drifting && !liveRect ? styles.drifting : '',
                settling && !liveRect ? styles.settling : '',
                expanded ? styles.expanded : '',
            ].join(' ')}
            style={compact || !box ? { zIndex: layer, transformOrigin: dockOrigin } : {
                zIndex: layer,
                transformOrigin: dockOrigin,
                width: box.width,
                height: box.height,
                transform: `translate3d(${Math.round(box.x)}px, ${Math.round(box.y)}px, 0)`,
            }}
            tabIndex={-1}
            aria-label={title}
            onPointerDownCapture={onFocus}
            onFocusCapture={onFocus}
            onPointerDown={handlePointerDown}
            onDragStart={(event) => event.preventDefault()}
        >
            <button type="button" className={styles.closeButton} onClick={close} aria-label={`${title} 닫기`}>
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                    <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.6" />
                </svg>
            </button>
            <div className={`${styles.sheet} ${styles[sheet]}`}>{children}</div>
            {!expanded && EDGES.map((edge) => (
                <span
                    key={edge}
                    className={`${styles.resizeHandle} ${styles[`edge-${edge}`]}`}
                    aria-hidden="true"
                    onPointerDown={handleResizeStart(edge)}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                    onPointerCancel={handleResizeEnd}
                    onLostPointerCapture={handleResizeEnd}
                />
            ))}
        </section>
    );
};

export default Window;
